import type React from 'react';
import { Droplets, Scissors, Sun, User, Smile, Wind, Shirt } from 'lucide-react';

/**
 * Grooming — the Personal domain's personal-care system (it took the `emotions`
 * area's slot). "The routine-based system for maintaining personal cleanliness,
 * appearance, scent, skin, hair, facial care, and presentation readiness."
 *
 * ## The sub-areas GROUP, they don't score
 * These seven are how the daily checklist is organised — nothing more. The owner
 * retired the per-sub-area status layer (the spec's second 7-list: Cleanliness,
 * Skin Condition, Oral Freshness…, once shown as a radar + status list): it
 * restated the same checkoffs a third time next to a centre panel that already
 * groups by sub-area. Grooming's standing is now just its XP (the rank card) and
 * its streak (Consistency) — the spec's 7th parameter, which was never an axis.
 */

export interface GroomingSubArea {
  id: string;
  /** The thing you do. */
  name: string;
  icon: React.ElementType;
  color: string;
}

/** The 7 sub-areas — the checklist's groups, in display order. */
export const GROOMING_SUB_AREAS: GroomingSubArea[] = [
  { id: 'hygiene', name: 'Hygiene', icon: Droplets, color: '#0ea5e9' },
  { id: 'hair', name: 'Hair', icon: Scissors, color: '#8b5cf6' },
  { id: 'skin', name: 'Skin', icon: Sun, color: '#f59e0b' },
  { id: 'face', name: 'Face', icon: User, color: '#ec4899' },
  { id: 'oral', name: 'Oral', icon: Smile, color: '#14b8a6' },
  { id: 'scent', name: 'Scent', icon: Wind, color: '#22c55e' },
  { id: 'presentation', name: 'Presentation', icon: Shirt, color: '#6366f1' },
];

export const GROOMING_SUB_AREA_IDS = GROOMING_SUB_AREAS.map(s => s.id);

export function getGroomingSubArea(id: string): GroomingSubArea | undefined {
  return GROOMING_SUB_AREAS.find(s => s.id === id);
}

/** A single daily checkoff, owned by one sub-area. */
export interface GroomingItem {
  id: string;
  name: string;
  /** Which sub-area this feeds (a `GROOMING_SUB_AREAS` id). */
  subAreaId: string;
  active: boolean;
}

/**
 * Seeded on first use; fully user-editable (mirrors `DEFAULT_PROTOCOLS`) — a
 * common daily core to trim and extend, not a prescription.
 *
 * ⚠️ **Every item must be genuinely DAILY.** Weekly/monthly grooming (haircut,
 * hair wash, exfoliation, the full reset) is the **Execute** half — Routines
 * tagged to this area — not a cadence on the checkoff, which has one shared
 * `scheduled` denominator per day. A 3×/week item parked here would be unchecked
 * four days in five: it would cap the daily XP below the cap *and* hold its
 * sub-area's standing under "Strong" forever. That is why Hair carries brushing
 * (daily) rather than washing (not).
 *
 * ⚠️ Item count is an implicit XP weight — each item is worth `1/scheduled` of
 * the daily cap — so a sub-area with three items is worth 3× a sub-area with
 * one. The split below keeps the heaviest (Skin, Oral) at ~23% of a day each
 * rather than the 30% Skin carried in v1.
 *
 * Ids are stable across retunes: an id that survives keeps its logged history.
 */
export const DEFAULT_GROOMING_ITEMS: GroomingItem[] = [
  { id: 'grm-shower', name: 'Shower', subAreaId: 'hygiene', active: true },
  { id: 'grm-hair-brush', name: 'Brush / comb hair', subAreaId: 'hair', active: true },
  { id: 'grm-hair', name: 'Style hair', subAreaId: 'hair', active: true },
  { id: 'grm-cleanse', name: 'Cleanse face', subAreaId: 'skin', active: true },
  { id: 'grm-moisturize', name: 'Moisturize', subAreaId: 'skin', active: true },
  { id: 'grm-sunscreen', name: 'Sunscreen', subAreaId: 'skin', active: true },
  { id: 'grm-shave', name: 'Shave / trim', subAreaId: 'face', active: true },
  { id: 'grm-teeth', name: 'Brush teeth', subAreaId: 'oral', active: true },
  { id: 'grm-floss', name: 'Floss', subAreaId: 'oral', active: true },
  { id: 'grm-mouthwash', name: 'Mouthwash', subAreaId: 'oral', active: true },
  { id: 'grm-deodorant', name: 'Deodorant', subAreaId: 'scent', active: true },
  { id: 'grm-presentation', name: 'Fresh clothes', subAreaId: 'presentation', active: true },
  { id: 'grm-mirror', name: 'Final mirror check', subAreaId: 'presentation', active: true },
];
